import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  Fade,
  Zoom,
  Tooltip,
  Divider,
  useTheme,
  alpha,
  CircularProgress,
  Alert,
  Snackbar,
  Fab,
  Badge,
  Tabs,
  Tab,
  InputAdornment,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Receipt as ReceiptIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Sort as SortIcon,
  AttachMoney as MoneyIcon,
  Category as CategoryIcon,
  DateRange as DateIcon,
  Description as DescriptionIcon,
  ArrowUpward as IncomeIcon,
  ArrowDownward as ExpenseIcon,
  CloudUpload as UploadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO } from 'date-fns';
import axios from 'axios';

function Transactions() {
  const theme = useTheme();
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentTransaction, setCurrentTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrDialogOpen, setOcrDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const [formData, setFormData] = useState({
    amount: '',
    description: '',
    transaction_date: format(new Date(), 'yyyy-MM-dd'),
    transaction_type: 'expense',
    category_id: '',
    source: 'manual',
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'

  useEffect(() => {
    fetchTransactions();
    fetchCategories();
  }, []);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://stu148664.webhosting.arden.ac.uk/COM6008/api/transactions');
      setTransactions(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch transactions');
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get('https://stu148664.webhosting.arden.ac.uk/COM6008/api/categories');
      setCategories(response.data);
    } catch (err) {
      setError('Failed to fetch categories');
    }
  };

  const handleOpen = (transaction = null) => {
    if (transaction) {
      setCurrentTransaction(transaction);
      setFormData({
        amount: transaction.amount,
        description: transaction.description,
        transaction_date: format(new Date(transaction.transaction_date), 'yyyy-MM-dd'),
        transaction_type: transaction.transaction_type,
        category_id: transaction.category_id,
        source: transaction.source,
      });
      setEditMode(true);
    } else {
      setFormData({
        amount: '',
        description: '',
        transaction_date: format(new Date(), 'yyyy-MM-dd'),
        transaction_type: 'expense',
        category_id: '',
        source: 'manual',
      });
      setEditMode(false);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentTransaction(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await axios.put(`https://stu148664.webhosting.arden.ac.uk/COM6008/api/transactions/${currentTransaction.id}`, formData);
        setSnackbar({ open: true, message: 'Transaction updated successfully', severity: 'success' });
      } else {
        await axios.post('https://stu148664.webhosting.arden.ac.uk/COM6008/api/transactions', formData);
        setSnackbar({ open: true, message: 'Transaction added successfully', severity: 'success' });
      }
      fetchTransactions();
      handleClose();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to save transaction', severity: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this transaction?')) {
      try {
        await axios.delete(`https://stu148664.webhosting.arden.ac.uk/COM6008/api/transactions/${id}`);
        setSnackbar({ open: true, message: 'Transaction deleted successfully', severity: 'success' });
        fetchTransactions();
      } catch (err) {
        setSnackbar({ open: true, message: 'Failed to delete transaction', severity: 'error' });
      }
    }
  };

  const handleReceiptUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setOcrLoading(true);
    setSelectedFile(file);
    setError(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch('https://stu148664.webhosting.arden.ac.uk/COM6008/api/ocr/process', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to process receipt');
      }

      // Create transaction with OCR data
      const transactionData = {
        amount: data.amount || 0,
        description: data.description || 'Receipt Purchase',
        transaction_date: data.date || new Date().toISOString().split('T')[0],
        category_id: data.category_id,
        transaction_type: 'expense',  // Default to expense for receipts
        user_id: 1  // Hardcoded for testing
      };

      // Create the transaction
      const createResponse = await fetch('https://stu148664.webhosting.arden.ac.uk/COM6008/api/transactions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(transactionData),
      });

      if (!createResponse.ok) {
        throw new Error('Failed to create transaction');
      }

      // Update form with OCR data
      setFormData(prev => ({
        ...prev,
        amount: data.amount || '',
        description: data.description || '',
        transaction_date: data.date || new Date().toISOString().split('T')[0],
        category_id: data.category_id || '',
        transaction_type: 'expense'  // Default to expense for receipts
      }));

      setOcrDialogOpen(false);
      setSelectedFile(null);
      setSnackbar({
        open: true,
        message: 'Receipt processed and transaction created successfully',
        severity: 'success'
      });

      // Refresh transactions list
      fetchTransactions();
    } catch (error) {
      console.error('OCR Error:', error);
      setError(error.message || 'Failed to process receipt. Please try again or enter details manually.');
      setSnackbar({
        open: true,
        message: error.message || 'Failed to process receipt',
        severity: 'error'
      });
    } finally {
      setOcrLoading(false);
    }
  };

  const filteredTransactions = transactions
    .filter(transaction => {
      const matchesSearch = transaction.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = filterType === 'all' || transaction.transaction_type === filterType;
      
      // Safe date comparison
      let matchesDate = true;
      if (selectedDate) {
        try {
          const transactionDate = transaction.transaction_date ? parseISO(transaction.transaction_date) : null;
          const selectedDateFormatted = format(selectedDate, 'yyyy-MM-dd');
          const transactionDateFormatted = transactionDate ? format(transactionDate, 'yyyy-MM-dd') : null;
          matchesDate = transactionDateFormatted === selectedDateFormatted;
        } catch (error) {
          console.error('Date comparison error:', error);
          matchesDate = false;
        }
      }
      
      return matchesSearch && matchesType && matchesDate;
    })
    .sort((a, b) => {
      const multiplier = sortOrder === 'asc' ? 1 : -1;
      switch (sortBy) {
        case 'amount':
          return (a.amount - b.amount) * multiplier;
        case 'date':
          return (new Date(a.transaction_date) - new Date(b.transaction_date)) * multiplier;
        default:
          return 0;
      }
    });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Transactions
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your income and expenses
          </Typography>
        </Box>

        {/* Filters and Search */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search transactions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  label="Type"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="income">Income</MenuItem>
                  <MenuItem value="expense">Expense</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Filter by date"
                  value={selectedDate}
                  onChange={setSelectedDate}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Sort by</InputLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  label="Sort by"
                >
                  <MenuItem value="date">Date</MenuItem>
                  <MenuItem value="amount">Amount</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                startIcon={<SortIcon />}
              >
                {sortOrder === 'asc' ? 'Ascending' : 'Descending'}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Transaction List */}
        <AnimatePresence>
          {filteredTransactions.map((transaction) => (
            <TransactionCard
              key={transaction.id}
              transaction={transaction}
              onEdit={handleOpen}
              onDelete={handleDelete}
            />
          ))}
        </AnimatePresence>

        {/* Empty State */}
        {filteredTransactions.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <InfoIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No transactions found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchQuery || filterType !== 'all' || selectedDate
                  ? 'Try adjusting your filters'
                  : 'Add your first transaction to get started'}
              </Typography>
            </Paper>
          </motion.div>
        )}

        {/* Add Transaction FAB */}
        <Tooltip title="Add Transaction">
          <Fab
            color="primary"
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 1000
            }}
            onClick={() => handleOpen()}
          >
            <AddIcon />
          </Fab>
        </Tooltip>

        {/* Receipt Upload FAB */}
        <Tooltip title="Scan Receipt">
          <Fab
            color="secondary"
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 100,
              zIndex: 1000
            }}
            onClick={() => setOcrDialogOpen(true)}
          >
            <ReceiptIcon />
          </Fab>
        </Tooltip>

        {/* Transaction Dialog */}
        <Dialog 
          open={open} 
          onClose={handleClose}
          maxWidth="sm"
          fullWidth
          TransitionComponent={Zoom}
        >
          <DialogTitle>
            {editMode ? 'Edit Transaction' : 'Add Transaction'}
          </DialogTitle>
          <DialogContent>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={formData.transaction_type}
                      onChange={(e) => setFormData({ ...formData, transaction_type: e.target.value })}
                      name="transaction_type"
                      label="Type"
                    >
                      <MenuItem value="income">Income</MenuItem>
                      <MenuItem value="expense">Expense</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Amount"
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Date"
                      value={formData.transaction_date ? parseISO(formData.transaction_date) : null}
                      onChange={(date) => setFormData({ ...formData, transaction_date: format(date, 'yyyy-MM-dd') })}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    options={categories}
                    getOptionLabel={(option) => option.name}
                    value={categories.find(c => c.id === formData.category_id) || null}
                    onChange={(event, newValue) => {
                      setFormData(prev => ({
                        ...prev,
                        category_id: newValue ? newValue.id : ''
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Category"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* OCR Dialog */}
        <Dialog 
          open={ocrDialogOpen} 
          onClose={() => setOcrDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          TransitionComponent={Zoom}
        >
          <DialogTitle>Scan Receipt</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="receipt-upload"
                type="file"
                onChange={handleReceiptUpload}
              />
              <label htmlFor="receipt-upload">
                <Button
                  variant="contained"
                  component="span"
                  startIcon={<UploadIcon />}
                  disabled={ocrLoading}
                >
                  {selectedFile ? 'Change File' : 'Upload Receipt'}
                </Button>
              </label>
              {selectedFile && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected: {selectedFile.name}
                </Typography>
              )}
              {ocrLoading && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              )}
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOcrDialogOpen(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </motion.div>
    </Container>
  );
}

const TransactionCard = ({ transaction, onEdit, onDelete }) => {
  const theme = useTheme();
  const isIncome = transaction.transaction_type === 'income';
  
  // Safe date formatting with error handling
  const formatTransactionDate = (dateString) => {
    try {
      if (!dateString) return 'Invalid Date';
      const date = parseISO(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return format(date, 'MMM dd, yyyy');
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Invalid Date';
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
    >
      <Card 
        elevation={2}
        sx={{
          mb: 2,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: theme.shadows[8],
            backgroundColor: alpha(theme.palette.background.paper, 0.9)
          }
        }}
      >
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <Chip
                  icon={isIncome ? <IncomeIcon /> : <ExpenseIcon />}
                  label={transaction.transaction_type}
                  color={isIncome ? 'success' : 'error'}
                  size="small"
                  sx={{ mr: 1 }}
                />
                <Typography variant="h6" component="div" noWrap>
                  {transaction.description}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {formatTransactionDate(transaction.transaction_date)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography 
                variant="h6" 
                color={isIncome ? 'success.main' : 'error.main'}
                sx={{ 
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                {isIncome ? '+' : '-'}${Math.abs(transaction.amount).toFixed(2)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {transaction.category?.name || 'Uncategorized'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Box display="flex" justifyContent="flex-end" gap={1}>
                <Tooltip title="Edit">
                  <IconButton 
                    size="small" 
                    onClick={() => onEdit(transaction)}
                    sx={{ color: theme.palette.primary.main }}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton 
                    size="small" 
                    onClick={() => onDelete(transaction.id)}
                    sx={{ color: theme.palette.error.main }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default Transactions; 